import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { setPreviewLanguage } from "../../../../../../redux/scanToSlice";
import { ScanToContext } from "..";

import { PropsType } from "./types";
import {
    DEFAULT_SECTION_TITLES,
    LANGUAGE_CODES,
    SECTION_ICONS,
    DEFAULT_ORDER_STATUSES,
    STATUS_LABELS,
} from "./constants";
import {
    CUSTOM_ICON_KEY,
    PREVIEW_MODE_WITH_ORDER,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import { useMultiCustomIcons } from "../shared/useMultiCustomIcons";
import {
    Container,
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
} from "./styled";
import { SectionTitleCard } from "./SectionTitleCard";
import { SectionIconCard } from "../shared/SectionIconCard";
import { OrderOptionsCard } from "./OrderOptionsCard";

// ─── Component ──────────────────────────────────────────

export function MaCommande({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    const sectionIcons = React.useMemo(
        () =>
            SECTION_ICONS.map((i) => ({
                key: i.key,
                label: t(i.labelKey),
                path: i.path,
            })),
        [t]
    );

    const defaultSectionTitles = DEFAULT_SECTION_TITLES;
    const defaultOrderStatuses = DEFAULT_ORDER_STATUSES;

    const { setSharedCommandeData } = React.useContext(ScanToContext);

    // ─── State ────────────────────────────────────────────
    // Cast: MyOrderConfig type doesn't declare every UI-state field used at
    // runtime (selectedLanguage, sectionTitles, etc.). Out of scope for ARCH-02.
    const savedOrder = useSelector(
        (state: RootState) => state.scanTo?.myOrder
    ) as any;
    // Editing + preview share ONE language: the shared previewLanguage, so
    // picking the section-title language here flips every section of the
    // preview. Aliased so existing call sites keep working unchanged.
    const dispatch = useDispatch();
    const previewLanguage = useSelector(
        (state: RootState) => state.scanTo.previewLanguage
    );
    const selectedLanguage = previewLanguage;
    const setSelectedLanguage = React.useCallback(
        (lang: string) => dispatch(setPreviewLanguage(lang)),
        [dispatch]
    );
    const [orderEnabled, setOrderEnabled] = React.useState(
        savedOrder?.orderEnabled ?? true
    );
    const [sectionTitleVisible, setSectionTitleVisible] = React.useState(
        savedOrder?.sectionTitleVisible ?? true
    );
    const [sectionTitles, setSectionTitles] = React.useState<
        Record<string, string>
    >(savedOrder?.sectionTitles || { ...defaultSectionTitles });
    const [selectedIcon, setSelectedIcon] = React.useState(
        savedOrder?.selectedIcon || "cart"
    );
    // Multi-icon state lifted into the shared hook so all 4 tabs
    // (Offres / Actions / MaCommande / Publicites) share one source of
    // truth — see `shared/useMultiCustomIcons.ts`.
    const {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
        resetCustomIcons,
    } = useMultiCustomIcons({
        saved: savedOrder,
        fallbackIconKey: "cart",
        setSelectedIcon,
    });

    const [showStepper, setShowStepper] = React.useState(
        savedOrder?.showStepper ?? true
    );
    const [showOrderDetails, setShowOrderDetails] = React.useState(
        savedOrder?.showOrderDetails ?? true
    );
    const [previewMode, setPreviewMode] = React.useState<
        "with_order" | "no_order"
    >(savedOrder?.previewMode || PREVIEW_MODE_WITH_ORDER);

    const [orderStatuses, setOrderStatuses] = React.useState(
        savedOrder?.fullOrderStatuses || defaultOrderStatuses
    );

    const [
        broadcastChannel,
        setBroadcastChannel,
    ] = React.useState<BroadcastChannel | null>(null);

    // ─── Resolved data ────────────────────────────────────

    const getStatusLabel = React.useCallback(
        (key: string): string => {
            return (
                STATUS_LABELS[key]?.[selectedLanguage] ||
                STATUS_LABELS[key]?.fr ||
                key
            );
        },
        [selectedLanguage]
    );

    // ─── BroadcastChannel init ────────────────────────────
    React.useEffect(() => {
        const channel = new BroadcastChannel("scan-to-preview-channel");
        setBroadcastChannel(channel);
        return () => {
            channel.close();
        };
    }, []);

    // ─── Share commande data via context + BroadcastChannel ──
    React.useEffect(() => {
        const previewData = {
            source: "commande",
            orderEnabled,
            sectionTitleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            showStepper,
            showOrderDetails,
            previewMode,
            orderStatuses: orderStatuses.map((s: any) => ({
                ...s,
                label: getStatusLabel(s.key),
            })),
            mainColor,
            selectedLanguage,
            // Full config for Redux persistence
            sectionTitles,
            selectedIcon,
            customIconUrls,
            customIconFiles: customIconUrls.map((url, i) => ({
                filename: customIconFilenames[i] || "",
                url,
            })),
            selectedCustomIdx,
            fullOrderStatuses: orderStatuses,
        };

        setSharedCommandeData(previewData);

        if (broadcastChannel) {
            try {
                broadcastChannel.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        sessionStorage.setItem(
            "scanToCommandePreviewData",
            JSON.stringify(previewData)
        );
    }, [
        orderEnabled,
        sectionTitleVisible,
        sectionTitles,
        selectedIcon,
        customIconUrl,
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        showStepper,
        showOrderDetails,
        previewMode,
        orderStatuses,
        selectedLanguage,
        mainColor,
        broadcastChannel,
        setSharedCommandeData,
        getStatusLabel,
        sectionIcons,
    ]);

    // (Blob URL cleanup is now inside useMultiCustomIcons.)

    // ─── Handlers ─────────────────────────────────────────

    const resetSectionTitle = () => {
        setSectionTitles({
            ...sectionTitles,
            [selectedLanguage]: defaultSectionTitles[selectedLanguage],
        });
    };

    const resetAllSectionTitles = () => {
        setSectionTitles({ ...defaultSectionTitles });
        setSectionTitleVisible(true);
        setSelectedLanguage("fr");
    };

    // (handleIconUpload + removeCustomIcon are provided by useMultiCustomIcons.)

    const resetAll = () => {
        setOrderEnabled(true);
        setShowStepper(true);
        setShowOrderDetails(true);
        setPreviewMode(PREVIEW_MODE_WITH_ORDER);
        setOrderStatuses([...defaultOrderStatuses]);
        setSectionTitleVisible(true);
        setSectionTitles({ ...defaultSectionTitles });
        setSelectedIcon("cart");
        resetCustomIcons();
        setSelectedLanguage("fr");
    };

    // ─── JSX ──────────────────────────────────────────────
    return (
        <Container data-testid="scan-to-order-container">
            {/* ─── Left: Configuration ─────────────────── */}
            <LeftSection data-testid="scan-to-order-left-section">
                {/* Toggle activation */}
                <SectionHeader>
                    <SectionTitle>{t("My order")}</SectionTitle>
                    <ToggleContainer>
                        <ToggleLabel>{t("Disable order section")}</ToggleLabel>
                        <Toggle
                            data-testid="scan-to-order-toggle-enabled"
                            isActive={orderEnabled}
                            mainColor={mainColor}
                            onClick={() => setOrderEnabled(!orderEnabled)}
                            aria-label={t("Disable order section")}
                        />
                    </ToggleContainer>
                </SectionHeader>

                {/* Section title (multilingual) */}
                <SectionTitleCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    defaultSectionTitles={defaultSectionTitles}
                    sectionTitles={sectionTitles}
                    onSectionTitleChange={(lang, value) =>
                        setSectionTitles({ ...sectionTitles, [lang]: value })
                    }
                    titleVisible={sectionTitleVisible}
                    onTitleVisibleToggle={() =>
                        setSectionTitleVisible(!sectionTitleVisible)
                    }
                    onResetSectionTitle={resetSectionTitle}
                    onResetAllSectionTitles={resetAllSectionTitles}
                />

                {/* Section icon */}
                <SectionIconCard
                    mode="multi"
                    testIdPrefix="scan-to-order"
                    mainColor={mainColor}
                    sectionIcons={sectionIcons}
                    selectedIcon={selectedIcon}
                    onIconSelect={setSelectedIcon}
                    customIconUrls={customIconUrls}
                    selectedCustomIdx={selectedCustomIdx}
                    onSelectCustomIcon={(idx) => {
                        setSelectedCustomIdx(idx);
                        setSelectedIcon(CUSTOM_ICON_KEY);
                    }}
                    iconFileInputRef={iconFileInputRef}
                    onIconUpload={handleIconUpload}
                    onRemoveCustomIcon={removeCustomIcon}
                />

                {/* Display options */}
                <OrderOptionsCard
                    mainColor={mainColor}
                    showStepper={showStepper}
                    onToggleStepper={() => setShowStepper(!showStepper)}
                    showOrderDetails={showOrderDetails}
                    onToggleOrderDetails={() =>
                        setShowOrderDetails(!showOrderDetails)
                    }
                    previewMode={previewMode}
                    onPreviewModeChange={setPreviewMode}
                    onResetAll={resetAll}
                />
            </LeftSection>
        </Container>
    );
}
